import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { Gift } from 'lucide-react';

const Signup = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    referral_code: '',
  });
  const [loading, setLoading] = useState(false);

  // Prefill referral code from ?ref= or ?ref_mob= query param
  useEffect(() => {
    const ref = searchParams.get('ref') || searchParams.get('ref_mob');
    const isMobRef = !!searchParams.get('ref_mob');
    if (ref) {
      setFormData(prev => ({
        ...prev,
        referral_code: ref.toUpperCase(),
        // If ref_mob present, default account type to mobilitier
        ...(isMobRef ? { role: 'mobilitier' } : {}),
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Las contraseñas no coinciden',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const { authAPI } = await import('../api');
      const response = await authAPI.register({
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        role: formData.role,
        referral_code: formData.referral_code.trim() || undefined,
      });
      
      // Store token and user
      localStorage.setItem('mobility_token', response.data.access_token);
      login(response.data.user);
      
      toast({
        title: '¡Cuenta creada!',
        description: formData.referral_code
          ? (formData.role === 'mobilitier'
              ? `Bienvenido, ${response.data.user.name}. Tu referidor recibirá comisión reducida cuando completes tu primera renta.`
              : `Bienvenido, ${response.data.user.name}. ¡$200 MXN en tu cuenta!`)
          : `Bienvenido a Mobility, ${response.data.user.name}`,
      });

      if (formData.role === 'mobilitier') {
        navigate('/mobilitier/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al crear cuenta',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-2 flex justify-center">
            <img src={require('../brand').BRAND.logos.wordmarkDark} alt="Mobility" className="h-14 w-auto" />
          </div>
          <CardTitle>Crear cuenta</CardTitle>
          <CardDescription>Únete a la comunidad Mobility</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Juan Pérez"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+52 555 123 4567"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="mb-3 block">Tipo de cuenta</Label>
              <RadioGroup value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="user" id="user" />
                  <Label htmlFor="user" className="cursor-pointer">Usuario (Rentar autos)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mobilitier" id="mobilitier" />
                  <Label htmlFor="mobilitier" className="cursor-pointer">Mobilitier (Rentar mis autos)</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="referral_code">
                Código de referido <span className="text-gray-400 text-xs">(opcional)</span>
              </Label>
              <div className="relative">
                <Gift className="w-4 h-4 absolute left-3 top-3 text-purple-500" />
                <Input
                  id="referral_code"
                  name="referral_code"
                  type="text"
                  value={formData.referral_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, referral_code: e.target.value.toUpperCase() }))}
                  placeholder="JUAN1RJ1"
                  maxLength={12}
                  className="pl-9 uppercase font-mono"
                  data-testid="signup-referral-input"
                />
              </div>
              {formData.referral_code && (
                formData.role === 'mobilitier' ? (
                  <p className="text-xs text-ink-700 mt-1 font-mont">
                    🤝 Programa B2B Mobilitier — Cuando completes tu primera renta, tu referidor obtiene 15% de comisión durante 90 días.
                  </p>
                ) : (
                  <p className="text-xs text-purple-600 mt-1">🎁 Recibirás $200 MXN al crear tu cuenta</p>
                )
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-[#bcef3c] hover:bg-[#a8d92e] text-gray-900 font-semibold"
              disabled={loading}
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-[#000000] hover:underline font-medium">
                Inicia sesión
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;